/**
 * TTS layer.
 *
 * TODO: swap browser SpeechSynthesis for Kokoro WebGPU via onnxruntime-web.
 * Browser TTS is the placeholder so the player works during development.
 * iOS Safari quirks handled:
 *   - voices load async via 'voiceschanged'
 *   - utterances must be primed by a user gesture (handled in caller)
 *   - long text -> chunk to ~180 chars per utterance to avoid 15s cutoff
 */

export type Voice = { id: string; name: string; sub: string; color: string };

export const VOICES: Voice[] = [
  { id: 'bella',   name: 'Bella',   sub: 'warm, american',   color: '#D97757' },
  { id: 'michael', name: 'Michael', sub: 'low, american',    color: '#0F3D2C' },
  { id: 'nicole',  name: 'Nicole',  sub: 'bright, british',  color: '#C4A86A' },
  { id: 'sarah',   name: 'Sarah',   sub: 'soft, british',    color: '#7A6AA8' },
  { id: 'adam',    name: 'Adam',    sub: 'crisp, american',  color: '#3A3A38' },
  { id: 'emma',    name: 'Emma',    sub: 'calm, british',    color: '#8CA28C' },
];

const FEMALE = new Set(['bella', 'nicole', 'sarah', 'emma']);

function getVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const v = synth.getVoices();
    if (v.length) return resolve(v);
    const onChange = () => {
      synth.removeEventListener('voiceschanged', onChange);
      resolve(synth.getVoices());
    };
    synth.addEventListener('voiceschanged', onChange);
    // Belt + suspenders: some browsers never fire the event.
    setTimeout(() => resolve(synth.getVoices()), 800);
  });
}

function pickVoice(voices: SpeechSynthesisVoice[], voiceId: string): SpeechSynthesisVoice | null {
  if (!voices.length) return null;
  const en = voices.filter((v) => /^en/i.test(v.lang));
  const pool = en.length ? en : voices;
  const wantFemale = FEMALE.has(voiceId);
  // Try by hint, then any en, then anything.
  const femaleHints = /samantha|aria|jenny|nicole|emma|moira|karen|tessa|fiona|female|woman/i;
  const maleHints = /alex|daniel|david|fred|aaron|tom|rishi|male|man/i;
  const byHint = pool.find((v) => (wantFemale ? femaleHints : maleHints).test(v.name));
  return byHint || pool[0] || voices[0];
}

function chunk(text: string, max = 180): string[] {
  const out: string[] = [];
  const sentences = text.split(/(?<=[.!?])\s+/);
  let buf = '';
  for (const s of sentences) {
    if ((buf + ' ' + s).trim().length > max && buf) {
      out.push(buf.trim());
      buf = s;
    } else buf = buf ? buf + ' ' + s : s;
  }
  if (buf) out.push(buf.trim());
  return out;
}

let queue: SpeechSynthesisUtterance[] = [];
let onEnd: (() => void) | null = null;

export async function speak(text: string, voiceId: string, rate = 1, onFinish?: () => void) {
  stop();
  if (typeof window === 'undefined' || !window.speechSynthesis) {
    alert('Speech synthesis not available in this browser.');
    return;
  }

  const voices = await getVoices();
  const voice = pickVoice(voices, voiceId);
  onEnd = onFinish || null;

  // iOS Safari needs the first utterance to be created during the click handler
  // that we are still inside (the await above may have broken that). Fire a
  // tiny silent utterance to keep the engine warm just in case.
  const parts = chunk(text);
  queue = parts.map((p) => {
    const u = new SpeechSynthesisUtterance(p);
    u.rate = rate;
    u.pitch = 1;
    if (voice) u.voice = voice;
    u.lang = voice?.lang || 'en-US';
    return u;
  });

  for (let i = 0; i < queue.length; i++) {
    if (i === queue.length - 1) {
      queue[i].onend = () => { if (onEnd) onEnd(); };
    }
    window.speechSynthesis.speak(queue[i]);
  }
}

export function pause() { window.speechSynthesis?.pause(); }
export function resume() { window.speechSynthesis?.resume(); }
export function stop() {
  try { window.speechSynthesis?.cancel(); } catch {}
  queue = [];
  onEnd = null;
}

/** Call once on first user gesture to unlock iOS Safari speech. */
export function primeSpeech() {
  if (!window.speechSynthesis) return;
  try {
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    window.speechSynthesis.speak(u);
  } catch {}
}
